import { useState } from "react";
import { Bell, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUserPreferences } from "@/hooks/useUserPreferences";

interface NotificationPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NotificationPrompt = ({ open, onOpenChange }: NotificationPromptProps) => {
  const { updateNotificationPermission, updatePushSubscription } = useUserPreferences();
  const [loading, setLoading] = useState(false);

  const handleSkip = async () => {
    await updateNotificationPermission("dismissed");
    onOpenChange(false);
  };

  const handleEnable = async () => {
    setLoading(true);
    
    if ("Notification" in window) {
      try {
        const permission = await Notification.requestPermission();
        
        if (permission === "granted") {
          // Register for push notifications
          await updatePushSubscription({ enabled: true, permission: "granted" });
          onOpenChange(false);
        } else if (permission === "denied") {
          await updateNotificationPermission("denied");
          onOpenChange(false);
        } else {
          // Permission was dismissed
          await updateNotificationPermission("dismissed");
          onOpenChange(false);
        }
      } catch (error) {
        console.error("Error requesting notification permission:", error);
        await updateNotificationPermission("denied");
        onOpenChange(false);
      }
    } else {
      // Notifications not supported
      await updateNotificationPermission("denied");
      onOpenChange(false);
    }
    
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Ativar Notificações
          </DialogTitle>
          <DialogDescription>
            Receba lembretes para manter seus hábitos em dia!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-center py-4">
            <div className="relative">
              <div className="w-20 h-20 bg-coral/20 rounded-full flex items-center justify-center">
                <Bell className="h-10 w-10 text-coral" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-coral rounded-full flex items-center justify-center animate-bounce">
                <span className="text-white text-xs font-bold">!</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                Lembretes diários para completar seus hábitos
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                Celebrações quando seu sapo evolui
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                Avisos sobre pedidos para entrar nas suas lagoas
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button onClick={handleEnable} className="w-full" disabled={loading}>
              <Bell className="h-4 w-4 mr-2" />
              {loading ? "Solicitando..." : "Ativar Notificações"}
            </Button>
            <Button variant="ghost" onClick={handleSkip} className="w-full" disabled={loading}>
              Agora não
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
